import {AxiosError} from "axios";
import {useLocation, useNavigate} from "react-router-dom";
import {get, isEqual, noop} from "lodash";
import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import {ViewController} from "data/types/structure";
import {ApiError} from "data/utils/api_error";
import {IApiResponse} from "data/services/http";
import {ModalType, RequestState} from "data/enums";
import {GIGYA_KEY, IS_SKIP_SSO} from "data/constants";
import {Bindings} from "data/constants/bindings";
import type {IUserStore} from "data/stores/user/user.store";
import type {IAPILogin, IGigyaResponse} from "data/providers/api/user.api.provider";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {extractErrorMessage} from "data/utils";
import type {IGigyaGateStore} from "data/stores/gigya_gate/gigya_gate.store";
import {ConnextraType, createConnextraScript} from "data/utils/createConnextraScript";

interface IProps {
	location: ReturnType<typeof useLocation>;
	navigate: ReturnType<typeof useNavigate>;
}

export interface IGigyaGateController extends ViewController<IProps> {
	get isLogoutLoading(): boolean;

	get isSessionChecked(): boolean;

	get isAuthorized(): boolean;
}

@injectable()
export class GigyaGateController implements IGigyaGateController {
	@observable private _location?: ReturnType<typeof useLocation>;
	@observable private _navigate?: ReturnType<typeof useNavigate>;
	@observable private _logoutRequestState = RequestState.IDLE;
	@observable private gigyaResponse?: IGigyaResponse;
	@observable private _isSessionChecked: boolean = false;

	get isLogoutLoading() {
		return isEqual(this._logoutRequestState, RequestState.PENDING);
	}

	get isSessionChecked() {
		return this._isSessionChecked;
	}

	get isAuthorized() {
		return this._userStore.isAuthorized;
	}

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.GigyaGateStore) readonly _gigyaGateStore: IGigyaGateStore
	) {
		makeAutoObservable(this);
	}

	@action onError = (error: AxiosError<IApiResponse>) => {
		this._modalsStore.showModal(ModalType.ERROR, {
			message: extractErrorMessage(error),
		});
	};

	@action private markSessionAsChecked = () => {
		this._isSessionChecked = true;
	};

	@action private handleLoginError = (error: AxiosError<IApiResponse>) => {
		if (isEqual(error.response?.status, ApiError.USER_NOT_REGISTERED)) {
			this._gigyaGateStore.setRedirectUrl(get(this._location, "pathname", ""));
			this._navigate?.("/registration", {replace: true});
			this._gigyaGateStore.setIsShowRegistrationPage(true);
		} else {
			this.onError(error);
		}
	};

	@action private APILogin = (params: IAPILogin) => {
		return this._userStore
			.login(params)
			.then(() => {
				createConnextraScript(ConnextraType.LOGIN, this._userStore.user?.id);
			})
			.catch(this.handleLoginError);
	};

	public goToMyAccount = () => {
		window.location.href = `https://id.nfl.com/account?redirectUrl=${window.location.href}`;
	};

	public SSOLogin = () => {
		window.gigya?.sso.login({
			authFlow: "redirect",
			apiKey: GIGYA_KEY,
		});
	};

	@action public SSOLogout = () => {
		window.gigya?.accounts.logout();
	};

	public initSSO() {
		this.getSSOUser()
			.then(this.APILogin)
			.catch(noop) // We don't won't to do anything if SSO doesn't return a user.
			.finally(this.markSessionAsChecked);

		window.gigya?.accounts.addEventHandlers({
			onLogout: this.handleSSOLogout,
		});
	}

	@action private handleSSOLogout = () => {
		this._logoutRequestState = RequestState.PENDING;

		this._userStore
			.logout()
			.then(this._userStore.clearUser)
			.catch(this.onError)
			.finally(() =>
				runInAction(() => {
					this._logoutRequestState = RequestState.SUCCESS;
				})
			);

		localStorage.removeItem("LP_tutorial");
		localStorage.removeItem("HP_tutorial");
	};

	@action private getSSOUser = () => {
		return new Promise<IAPILogin>((resolve, reject) => {
			window.gigya?.accounts.getAccountInfo({
				callback: (resp) => {
					if (resp.errorCode) {
						runInAction(() => {
							reject(resp);
						});

						return;
					}

					runInAction(() => {
						const loginParams = {
							email: resp.profile.email,
							gigyaId: resp.UID,
							gigyaSignature: resp.UIDSignature,
							gigyaSignatureTimestamp: resp.signatureTimestamp,
						};

						this._userStore.setUserParams(loginParams);
						this._userStore.setUserProfile(resp.profile);

						this.gigyaResponse = {
							...resp.profile,
							...loginParams,
						};

						resolve(loginParams);
					});
				},
			});
		});
	};

	@action onChange({navigate, location}: IProps) {
		this._location = location;
		this._navigate = navigate;
	}

	dispose() {
		return;
	}

	init(params: IProps) {
		this.onChange(params);

		if (IS_SKIP_SSO) {
			this._userStore.requestUser().then(this.markSessionAsChecked).catch(this.onError);
		} else {
			this.initSSO();
		}
	}
}
